import { useState, useEffect } from "react";

interface Submission {
  id: string;
  type: "restaurant" | "accommodation" | "gold";
  name: string;
  ownerName: string;
  email: string;
  phone: string;
  city: string;
  extra: string;
  status: "pending" | "approved" | "rejected";
  appliedAt: string;
}

const TYPE_LABELS: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  restaurant: { label: "Restoran", icon: "ri-store-2-line", color: "text-amber-600", bg: "bg-amber-50" },
  accommodation: { label: "Konaklama", icon: "ri-hotel-line", color: "text-stone-600", bg: "bg-stone-100" },
  gold: { label: "Gold Üyelik", icon: "ri-vip-crown-line", color: "text-amber-500", bg: "bg-amber-50" },
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: "Bekliyor", color: "bg-amber-100 text-amber-700" },
  approved: { label: "Onaylandı", color: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Reddedildi", color: "bg-rose-100 text-rose-700" },
};

const SubmissionHistory = () => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [filterType, setFilterType] = useState<"all" | "restaurant" | "accommodation" | "gold">("all");
  const [filterStatus, setFilterStatus] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"newest" | "oldest">("newest");
  const [selectedSub, setSelectedSub] = useState<Submission | null>(null);

  useEffect(() => {
    const restaurants: Submission[] = (JSON.parse(localStorage.getItem("pendingRestaurants") || "[]") as {
      id: string; restaurantName: string; ownerName: string; email: string; phone: string;
      city: string; cuisine: string; status: string; appliedAt: string;
    }[]).map((r) => ({
      id: r.id,
      type: "restaurant" as const,
      name: r.restaurantName,
      ownerName: r.ownerName,
      email: r.email,
      phone: r.phone,
      city: r.city,
      extra: r.cuisine,
      status: (r.status as "pending" | "approved" | "rejected") || "pending",
      appliedAt: r.appliedAt,
    }));

    const accommodations: Submission[] = (JSON.parse(localStorage.getItem("pendingAccommodations") || "[]") as {
      id: string; businessName: string; ownerName: string; email: string; phone: string;
      city: string; businessType: string; status: string; appliedAt: string;
    }[]).map((a) => ({
      id: a.id,
      type: "accommodation" as const,
      name: a.businessName,
      ownerName: a.ownerName,
      email: a.email,
      phone: a.phone,
      city: a.city,
      extra: a.businessType,
      status: (a.status as "pending" | "approved" | "rejected") || "pending",
      appliedAt: a.appliedAt,
    }));

    // Gold başvuruları localStorage'da tutulmadığı için form geçmişinden simüle ediyoruz
    const goldRaw = JSON.parse(localStorage.getItem("goldSubmissions") || "[]") as Submission[];

    const all = [...restaurants, ...accommodations, ...goldRaw].sort(
      (a, b) => new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime()
    );
    setSubmissions(all);
  }, []);

  const filtered = submissions
    .filter((s) => filterType === "all" || s.type === filterType)
    .filter((s) => filterStatus === "all" || s.status === filterStatus)
    .filter((s) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        s.name.toLowerCase().includes(q) ||
        s.ownerName.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        s.city.toLowerCase().includes(q)
      );
    })
    .sort((a, b) =>
      sortBy === "newest"
        ? new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime()
        : new Date(a.appliedAt).getTime() - new Date(b.appliedAt).getTime()
    );

  const totalCount = submissions.length;
  const pendingCount = submissions.filter((s) => s.status === "pending").length;
  const approvedCount = submissions.filter((s) => s.status === "approved").length;
  const rejectedCount = submissions.filter((s) => s.status === "rejected").length;

  const restCount = submissions.filter((s) => s.type === "restaurant").length;
  const accCount = submissions.filter((s) => s.type === "accommodation").length;
  const goldCount = submissions.filter((s) => s.type === "gold").length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Başvuru Geçmişi
          </h2>
          <p className="text-stone-400 text-xs mt-0.5">Tüm form gönderimlerinin kaydı</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as "newest" | "oldest")}
            className="text-xs border border-stone-200 rounded-lg px-3 py-2 text-stone-600 outline-none focus:border-amber-400 bg-white cursor-pointer"
          >
            <option value="newest">En Yeni</option>
            <option value="oldest">En Eski</option>
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-stone-100 p-4 text-center">
          <p className="text-2xl font-bold text-stone-900">{totalCount}</p>
          <p className="text-xs text-stone-500 mt-1">Toplam Başvuru</p>
        </div>
        <div
          className="bg-amber-50 rounded-xl border border-amber-100 p-4 text-center cursor-pointer hover:border-amber-300 transition-colors"
          onClick={() => setFilterStatus(filterStatus === "pending" ? "all" : "pending")}
        >
          <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
          <p className="text-xs text-stone-500 mt-1">Bekleyen</p>
        </div>
        <div
          className="bg-emerald-50 rounded-xl border border-emerald-100 p-4 text-center cursor-pointer hover:border-emerald-300 transition-colors"
          onClick={() => setFilterStatus(filterStatus === "approved" ? "all" : "approved")}
        >
          <p className="text-2xl font-bold text-emerald-600">{approvedCount}</p>
          <p className="text-xs text-stone-500 mt-1">Onaylanan</p>
        </div>
        <div
          className="bg-rose-50 rounded-xl border border-rose-100 p-4 text-center cursor-pointer hover:border-rose-300 transition-colors"
          onClick={() => setFilterStatus(filterStatus === "rejected" ? "all" : "rejected")}
        >
          <p className="text-2xl font-bold text-rose-600">{rejectedCount}</p>
          <p className="text-xs text-stone-500 mt-1">Reddedilen</p>
        </div>
      </div>

      {/* Type breakdown */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { type: "restaurant", label: "Restoran", count: restCount, icon: "ri-store-2-line", color: "text-amber-500" },
          { type: "accommodation", label: "Konaklama", count: accCount, icon: "ri-hotel-line", color: "text-stone-500" },
          { type: "gold", label: "Gold Üyelik", count: goldCount, icon: "ri-vip-crown-line", color: "text-amber-500" },
        ].map((item) => (
          <button
            key={item.type}
            onClick={() => setFilterType(filterType === item.type as typeof filterType ? "all" : item.type as typeof filterType)}
            className={`flex items-center gap-3 p-4 rounded-xl border transition-all cursor-pointer text-left ${
              filterType === item.type ? "border-amber-400 bg-amber-50" : "border-stone-100 bg-white hover:border-stone-200"
            }`}
          >
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${filterType === item.type ? "bg-amber-100" : "bg-stone-100"}`}>
              <i className={`${item.icon} ${item.color} text-lg`}></i>
            </div>
            <div>
              <p className="text-lg font-bold text-stone-900">{item.count}</p>
              <p className="text-xs text-stone-500">{item.label}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex-1 min-w-48 flex items-center bg-white border border-stone-200 rounded-lg px-3 gap-2 focus-within:border-amber-400 transition-colors">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-search-line text-stone-400 text-sm"></i>
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="İsim, e-posta veya şehir ara..."
            className="flex-1 bg-transparent text-sm text-stone-700 outline-none py-2.5"
          />
          {search && (
            <button onClick={() => setSearch("")} className="w-4 h-4 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer">
              <i className="ri-close-line text-sm"></i>
            </button>
          )}
        </div>

        <div className="flex items-center bg-stone-100 rounded-full p-1 gap-1">
          {(["all", "pending", "approved", "rejected"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                filterStatus === s ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"
              }`}
            >
              {s === "all" ? "Tümü" : s === "pending" ? "Bekleyen" : s === "approved" ? "Onaylanan" : "Reddedilen"}
            </button>
          ))}
        </div>

        {(filterType !== "all" || filterStatus !== "all" || search) && (
          <button
            onClick={() => { setFilterType("all"); setFilterStatus("all"); setSearch(""); }}
            className="flex items-center gap-1 text-xs px-3 py-1.5 bg-stone-200 text-stone-600 rounded-full hover:bg-stone-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-line"></i> Filtreleri Temizle
          </button>
        )}
      </div>

      {/* Results count */}
      <p className="text-xs text-stone-400 mb-3">
        {filtered.length} başvuru gösteriliyor
        {(filterType !== "all" || filterStatus !== "all" || search) && ` (${totalCount} toplam)`}
      </p>

      {/* List + Detail */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-stone-100 rounded-full">
            <i className="ri-file-list-3-line text-stone-400 text-2xl"></i>
          </div>
          <p className="text-stone-500 font-medium text-sm">
            {totalCount === 0 ? "Henüz başvuru yok" : "Bu filtreye uygun başvuru bulunamadı"}
          </p>
          <p className="text-stone-400 text-xs mt-1">
            {totalCount === 0
              ? "Restoran, konaklama veya Gold başvuruları burada görünecek"
              : "Farklı filtreler deneyin"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* List */}
          <div className="lg:col-span-1 space-y-2 max-h-[600px] overflow-y-auto pr-1">
            {filtered.map((sub) => {
              const typeInfo = TYPE_LABELS[sub.type];
              const statusInfo = STATUS_LABELS[sub.status];
              return (
                <button
                  key={sub.id}
                  onClick={() => setSelectedSub(sub)}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    selectedSub?.id === sub.id
                      ? "border-amber-400 bg-amber-50"
                      : "border-stone-100 bg-white hover:border-amber-200"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${typeInfo.bg}`}>
                      <i className={`${typeInfo.icon} ${typeInfo.color} text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1 mb-0.5">
                        <p className="text-sm font-semibold text-stone-900 truncate">{sub.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <p className="text-xs text-stone-500 truncate">{sub.ownerName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs font-medium ${typeInfo.color}`}>{typeInfo.label}</span>
                        <span className="text-xs text-stone-400">·</span>
                        <span className="text-xs text-stone-400">{sub.city}</span>
                      </div>
                      <p className="text-xs text-stone-300 mt-1">
                        {new Date(sub.appliedAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Detail */}
          <div className="lg:col-span-2">
            {selectedSub ? (
              <div className="bg-white rounded-2xl border border-stone-100 p-6 sticky top-4">
                {/* Header */}
                <div className="flex items-start gap-4 mb-6 pb-5 border-b border-stone-100">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0 ${TYPE_LABELS[selectedSub.type].bg}`}>
                    <i className={`${TYPE_LABELS[selectedSub.type].icon} ${TYPE_LABELS[selectedSub.type].color} text-xl`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {selectedSub.name}
                      </h3>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${STATUS_LABELS[selectedSub.status].color}`}>
                        {STATUS_LABELS[selectedSub.status].label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-semibold ${TYPE_LABELS[selectedSub.type].color}`}>
                        {TYPE_LABELS[selectedSub.type].label}
                      </span>
                      <span className="text-stone-300">·</span>
                      <span className="text-xs text-stone-500">{selectedSub.city}</span>
                      {selectedSub.extra && (
                        <>
                          <span className="text-stone-300">·</span>
                          <span className="text-xs text-stone-500">{selectedSub.extra}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-3 mb-5">
                  {[
                    { label: "Yetkili / Sahibi", value: selectedSub.ownerName, icon: "ri-user-line" },
                    { label: "E-posta", value: selectedSub.email, icon: "ri-mail-line" },
                    { label: "Telefon", value: selectedSub.phone, icon: "ri-phone-line" },
                    { label: "Şehir", value: selectedSub.city, icon: "ri-map-pin-line" },
                    { label: "Başvuru Tarihi", value: new Date(selectedSub.appliedAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }), icon: "ri-calendar-line" },
                    { label: "Başvuru Türü", value: TYPE_LABELS[selectedSub.type].label, icon: TYPE_LABELS[selectedSub.type].icon },
                  ].map((item) => (
                    <div key={item.label} className="bg-stone-50 rounded-xl p-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${item.icon} text-stone-400 text-xs`}></i>
                        </div>
                        <p className="text-xs text-stone-400">{item.label}</p>
                      </div>
                      <p className="text-sm font-semibold text-stone-800 truncate">{item.value || "—"}</p>
                    </div>
                  ))}
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2 flex-wrap pt-4 border-t border-stone-100">
                  <a
                    href={`mailto:${selectedSub.email}?subject=Başvurunuz Hakkında — ${selectedSub.name}`}
                    className="flex items-center gap-1.5 text-xs px-4 py-2 bg-stone-900 text-white rounded-xl hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-mail-send-line"></i> E-posta Gönder
                  </a>
                  {selectedSub.type === "restaurant" && (
                    <a
                      href="/admin"
                      className="flex items-center gap-1.5 text-xs px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-store-2-line"></i> Başvurular Sekmesine Git
                    </a>
                  )}
                  {selectedSub.type === "accommodation" && (
                    <a
                      href="/admin"
                      className="flex items-center gap-1.5 text-xs px-4 py-2 bg-stone-50 text-stone-700 border border-stone-200 rounded-xl hover:bg-stone-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-hotel-line"></i> Konaklama Sekmesine Git
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-stone-100 h-64 flex flex-col items-center justify-center">
                <div className="w-12 h-12 flex items-center justify-center mb-3">
                  <i className="ri-file-list-3-line text-stone-300 text-3xl"></i>
                </div>
                <p className="text-stone-400 text-sm">Detayları görmek için bir başvuru seçin</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionHistory;
